import React, { useState } from 'react';
import {
  FormControl, Select, MenuItem, InputLabel, Box, Button, Popover
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DateRangeIcon from '@material-ui/icons/DateRange';
import DateRangePicker from './DateRangePicker';

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 180,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: '#ffffff',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#059669',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#059669',
      },
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#059669',
    },
  },
  select: {
    fontWeight: 500,
  },
  modifyButton: {
    borderRadius: 10,
    textTransform: 'none',
    borderColor: '#e2e8f0',
    color: '#475569',
    fontWeight: 500,
    '&:hover': {
      borderColor: '#059669',
      backgroundColor: '#f0fdf4',
    },
  },
  popover: {
    '& .MuiPopover-paper': {
      borderRadius: 16,
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      border: '1px solid #e2e8f0',
      marginTop: 8,
    },
  },
  menuItem: {
    '&:hover': {
      backgroundColor: '#f0fdf4',
    },
    '&.Mui-selected': {
      backgroundColor: '#d1fae5',
      '&:hover': {
        backgroundColor: '#a7f3d0',
      },
    },
  },
}));

const PERIODS = [
  { value: 'week', label: 'Cette semaine' },
  { value: 'month', label: 'Ce mois' },
  { value: 'quarter', label: 'Ce trimestre' },
  { value: 'custom', label: 'Personnalisé' },
];

const PeriodSelector = ({ value, onChange, customDates, onCustomDatesChange }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSelectChange = (e) => {
    const newValue = e.target.value;
    if (newValue === 'custom') {
      setAnchorEl(e.currentTarget);
    } else {
      onChange(newValue);
    }
  };

  const handleCustomClick = (e) => {
    if (value === 'custom') {
      setAnchorEl(e.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApplyCustomDates = (dates) => {
    onCustomDatesChange(dates);
    onChange('custom');
    handleClose();
  };

  const formatDateRange = () => {
    if (customDates?.startDate && customDates?.endDate) {
      const start = new Date(customDates.startDate);
      const end = new Date(customDates.endDate);
      const formatDate = (d) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    return 'Personnalisé';
  };

  const open = Boolean(anchorEl);

  return (
    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
      <FormControl variant="outlined" size="small" className={classes.formControl}>
        <InputLabel>Période</InputLabel>
        <Select
          value={value}
          onChange={handleSelectChange}
          label="Période"
          className={classes.select}
        >
          {PERIODS.map((p) => (
            <MenuItem
              key={p.value}
              value={p.value}
              className={classes.menuItem}
            >
              {p.value === 'custom' && value === 'custom' ? formatDateRange() : p.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {value === 'custom' && (
        <Button
          size="small"
          variant="outlined"
          onClick={handleCustomClick}
          startIcon={<DateRangeIcon />}
          className={classes.modifyButton}
        >
          Modifier
        </Button>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className={classes.popover}
      >
        <DateRangePicker
          startDate={customDates?.startDate}
          endDate={customDates?.endDate}
          onApply={handleApplyCustomDates}
          onCancel={handleClose}
        />
      </Popover>
    </Box>
  );
};

export default PeriodSelector;
